import React, { CSSProperties } from 'react';
import type { Starter } from './StarterStep';

/**
 * A starter's thumbnail (UX review v10, StarterThumb): the home page of each
 * template drawn in its own type, palette and shapes — a sticker-and-arch
 * salon, a dark restaurant hero, a newspaper masthead — and, for the other
 * pages, a generic drawing of what that page does (a price list, a pricing
 * table, an archive, a donate box). Designed at ~300×200 and scaled with
 * CSS zoom by whoever shows it. Everything is inline so it survives zoom.
 *
 * The template fonts come from Google (the app loads them once, see
 * TEMPLATE_FONTS_HREF); the fallbacks keep the drawing legible without them.
 */

export const TEMPLATE_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Archivo:wght@400;800&family=IBM+Plex+Mono:wght@400&family=Gloock&family=Outfit:wght@400;600&family=DM+Serif+Display:ital@0;1&family=Manrope:wght@500;700&family=Bodoni+Moda:ital,wght@0,500;1,400&family=Jost:wght@400;500&family=Bricolage+Grotesque:wght@400;800&family=Instrument+Serif:ital@0;1&family=Newsreader:wght@400&family=Anton&family=Source+Serif+4:wght@400;600&family=Archivo+Narrow:wght@600&family=Barlow+Condensed:wght@700;800&family=Barlow:wght@500&family=Schibsted+Grotesk:wght@500;700;800&family=Literata:wght@400&display=swap';

const MONO = "'IBM Plex Mono', ui-monospace, monospace";

const FONT_STACKS: Record<string, string> = {
  serif: 'Georgia, "Times New Roman", serif',
  sans: '"Geist", system-ui, -apple-system, "Segoe UI", sans-serif',
  geist: '"Geist", Inter, system-ui, sans-serif',
  display: 'Georgia, "Iowan Old Style", "Times New Roman", serif',
  mono: MONO,
};

/** A site font (a preset name or a Google family) as a CSS stack. */
export function fontStack(v: string, fallback: 'serif' | 'sans' = 'sans'): string {
  if (FONT_STACKS[v]) return FONT_STACKS[v];
  return `'${v}', ${fallback === 'serif' ? 'Georgia, serif' : 'system-ui, sans-serif'}`;
}

const RADII: Record<string, [string, string]> = {
  round: ['999px', '8px'],
  soft: ['4px', '4px'],
  square: ['1px', '1px'],
};

/** Which drawing a page gets; the home page draws the template's own front. */
const PAGE_KINDS: Record<string, string> = {
  Work: 'work',
  Menu: 'menu',
  Pricing: 'pricing',
  Services: 'services',
  'What we do': 'services',
  About: 'about',
  Studio: 'about',
  Story: 'about',
  Contact: 'contact',
  Visit: 'contact',
  Tips: 'contact',
  Newsletters: 'contact',
  'Get involved': 'cause',
  Archive: 'list',
};

/** Two photos per template: the front and a second for sub-pages. */
const IMG: Record<string, [string, string]> = {
  studio: ['1600585154340-be6161a56a0c', '1524758631624-e2822e304c36'],
  local: ['1521590832167-7bcbfaa6381f', '1540555700478-4be289fbecef'],
  consultant: ['1573496359142-b8d87734a5a2', '1580489944761-15a19d654956'],
  restaurant: ['1517248135467-4c7edcad34c4', '1506794778202-cad84cf45f1d'],
  product: ['1573496359142-b8d87734a5a2', '1522071820081-009f0129c71c'],
  journal: ['1470071459604-3b5ec3a7fe05', '1438761681033-6461ffad8d80'],
  magazine: ['1519501025264-65ba15a82390', '1504711434969-e33886168f5c'],
  publisher: ['1449824913935-59a10b8d2000', '1495020689067-958852a7765e'],
  newsroom: ['1504711434969-e33886168f5c', '1495020689067-958852a7765e'],
  nonprofit: ['1441974231531-c6227db76b6e', '1501854140801-50d01698950b'],
};
const u = (id: string, w = 700) => `https://images.unsplash.com/photo-${id}?w=${w}&q=75&auto=format&fit=crop`;

const SERVICES: Record<string, { n: string; d: string; p: string }[]> = {
  local: [
    { n: 'Cut & blow-dry', d: 'Includes consultation and wash', p: '£48' },
    { n: 'Balayage', d: 'Hand-painted, with gloss', p: '£165' },
    { n: 'Signature facial', d: 'Cleanse, massage, mask', p: '£70' },
  ],
  consultant: [
    { n: 'Strategy sprint', d: 'Four weeks, one clear plan', p: '£12k' },
    { n: 'Monthly advisor', d: 'Fortnightly calls, message any time', p: '£3k/mo' },
    { n: 'Leadership workshop', d: 'Half a day, on site', p: '£2.5k' },
  ],
  nonprofit: [
    { n: 'Tree nursery', d: '12,000 native saplings a year', p: 'Join' },
    { n: 'River clean-ups', d: 'Monthly, along 31 km', p: 'Join' },
    { n: 'Trails', d: 'Forty miles kept open', p: 'Adopt' },
  ],
};
const SERVICES_DEFAULT = [
  { n: 'Service one', d: 'What it is, in a line', p: '£40' },
  { n: 'Service two', d: 'What it is, in a line', p: '£60' },
];

const ABOUT_LINE: Record<string, string> = {
  studio: 'Nine designers, writers and developers in Copenhagen and Lisbon.',
  local: 'We opened in 2017 with two chairs, one sink and a very good kettle.',
  consultant: 'Twelve years running operations at growing companies.',
  restaurant: 'Rui grew up above his father’s fish stall.',
  product: 'We think getting paid should be the easy part.',
  journal: 'I write things down so I remember them.',
  magazine: 'Independent, reader-funded, printed monthly since 1998.',
  publisher: 'A newsroom of 24 covering the city every day.',
  newsroom: 'Every story gets the space it earns.',
  nonprofit: 'Started by six neighbours with spades. Now 1,240 of us.',
};

const LOGO_FONT: Record<string, string> = {
  studio: "800 9px 'Archivo', sans-serif",
  local: "400 11px 'Gloock', serif",
  consultant: "400 9px 'DM Serif Display', serif",
  restaurant: "500 8px 'Bodoni Moda', serif",
  product: "800 10px 'Bricolage Grotesque', sans-serif",
  journal: "italic 400 11px 'Instrument Serif', serif",
  magazine: "400 10px 'Anton', sans-serif",
  publisher: "800 10px 'Schibsted Grotesk', sans-serif",
  newsroom: "800 10px 'Bricolage Grotesque', sans-serif",
  nonprofit: "800 9px 'Barlow Condensed', sans-serif",
};

function luminance(hex: string): number {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return 0;
  const [r, g, b] = [0, 2, 4].map((i) => {
    const c = parseInt(m[1].slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function thumbPalette(starter: Starter) {
  const { colors } = starter.look;
  return {
    bg: colors.background,
    text: colors.text,
    sub: colors.muted,
    line: `color-mix(in srgb, ${colors.muted} 38%, ${colors.background})`,
    accent: colors.accent,
    accent2: colors.accent2 || colors.accent,
    on: luminance(colors.accent) > 0.45 ? colors.background : '#ffffff',
  };
}

/** The wordmark size that fits `len` characters in ~270px at `per` em per character. */
const fit = (len: number, per: number, max: number, min: number) =>
  `${Math.round(Math.min(max, Math.max(min, 270 / (Math.max(len, 3) * per))))}px`;

type NavItem = { t: string; on: boolean; go: (e: React.MouseEvent) => void };

export function StarterThumb({
  starter,
  page = 'Home',
  siteName,
  siteAddr,
  onPage,
  tagline = '',
  titles = [],
  coverUrl = '',
}: {
  starter: Starter;
  page?: string;
  siteName: string;
  siteAddr: string;
  /** Tapping a page name in the drawing switches to that page. */
  onPage?: (page: string) => void;
  /**
   * The site's own words and photo in the template's composition (the Design
   * page): its tagline for the template's, its latest headlines for the sample
   * ones, its cover photo for the template's front photo. Unset (signup, where
   * the site has nothing yet), the template's own copy and photo are drawn —
   * which is what its pages carry.
   */
  tagline?: string;
  titles?: string[];
  coverUrl?: string;
}) {
  const id = starter.id;
  const c = thumbPalette(starter);
  const site = siteName || starter.name;
  const line = tagline.trim() || starter.tagline;
  /** The site's headline `i`, else the template's sample. */
  const h = (i: number, sample: string) => titles[i] || sample;
  const head = fontStack(starter.look.fonts.heading, 'serif');
  const body = fontStack(starter.look.fonts.body, 'sans');
  const [btnR, cardR] = RADII[starter.look.corners] || RADII.soft;
  const home = page === 'Home';
  const kind = home ? 'home' : PAGE_KINDS[page] || 'about';
  const hero = home ? line : page;
  const pick = (t: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onPage?.(t);
  };
  const nav: NavItem[] = starter.pages
    .map((p) => p.title)
    .slice(1, 4)
    .map((t) => ({ t, on: t === page, go: pick(t) }));
  const last = starter.pages[starter.pages.length - 1]?.title || 'Home';
  const img = coverUrl || (IMG[id] ? u(IMG[id][0]) : '');
  const img2 = IMG[id] ? u(IMG[id][1], 500) : '';
  const navLinks = (style: CSSProperties, onStyle?: CSSProperties) =>
    nav.map((n) => (
      <span key={n.t} onClick={n.go} style={{ cursor: 'pointer', textDecoration: n.on ? 'underline' : 'none', ...style, ...(n.on ? onStyle : {}) }}>
        {n.t}
      </span>
    ));

  const root: CSSProperties = {
    width: '100%',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    background: c.bg,
    color: c.text,
    fontFamily: body,
    textAlign: 'left',
  };

  // ── The home page: each template's own front ──────────────────────────
  if (home && id === 'studio') {
    return (
      <div style={{ ...root, display: 'flex', flexDirection: 'column', background: '#fff', color: '#0e0e0e', fontFamily: "'Archivo', sans-serif" }} aria-hidden="true">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', borderBottom: '0.5px solid #0e0e0e', fontSize: 5.5, fontWeight: 500 }}>
          <span style={{ fontWeight: 800, fontSize: 7 }}>{site}®</span>
          <span style={{ display: 'flex', gap: 7 }}>{navLinks({})}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 3, font: `400 4.5px ${MONO}` }}><i style={{ width: 4, height: 4, borderRadius: '50%', background: '#2fa35a' }} />Booking 2027</span>
        </div>
        <div style={{ padding: '6px 10px 0', fontWeight: 800, fontSize: fit(site.length, 0.66, 46, 14), lineHeight: 0.8, letterSpacing: '-0.06em', whiteSpace: 'nowrap', overflow: 'hidden' }}>{site.toLowerCase()}</div>
        <div style={{ display: 'flex', gap: 10, margin: '0 10px', padding: '6px 0', alignItems: 'flex-start', borderBottom: '0.5px solid #0e0e0e' }}>
          <span style={{ flex: 1, fontSize: 7, lineHeight: 1.1, fontWeight: 500 }}>{line}</span>
          <span style={{ font: `400 4.5px/1.6 ${MONO}` }}>Studio<br />Since 2014</span>
        </div>
        <div style={{ flex: 1, minHeight: 0, margin: '6px 10px 8px', position: 'relative', overflow: 'hidden', background: '#efefef' }}>
          <img src={img} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          <span style={{ position: 'absolute', right: 6, bottom: 6, width: 22, height: 22, borderRadius: '50%', background: '#fff', color: '#0e0e0e', fontSize: 4.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>View →</span>
        </div>
      </div>
    );
  }
  if (home && id === 'local') {
    return (
      <div style={{ ...root, display: 'flex', flexDirection: 'column', background: '#f7d9d2', color: '#2a1418', fontFamily: "'Outfit', sans-serif" }} aria-hidden="true">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 10px' }}>
          <span style={{ font: "400 11px/1 'Gloock', serif", color: '#8c1c31' }}>{site.toLowerCase()}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 5, fontWeight: 500 }}>{navLinks({})}<span style={{ padding: '3px 6px', borderRadius: 999, background: '#8c1c31', color: '#fff8f0', fontWeight: 600 }}>Book now</span></span>
        </div>
        <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '1.15fr 1fr', gap: 10, padding: '2px 10px 10px', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ alignSelf: 'flex-start', padding: '2px 5px', borderRadius: 999, background: '#ffd36e', fontSize: 4.5, fontWeight: 600 }}>Open today until 7pm</span>
            <span style={{ font: "400 15px/0.95 'Gloock', serif", color: '#8c1c31', letterSpacing: '-0.02em' }}>{line}</span>
            <span style={{ display: 'flex', gap: 3 }}><span style={{ padding: '3px 6px', borderRadius: 999, background: '#8c1c31', color: '#fff8f0', fontSize: 4.5, fontWeight: 600 }}>Book an appointment</span><span style={{ padding: '3px 6px', borderRadius: 999, border: '0.5px solid #8c1c31', color: '#8c1c31', fontSize: 4.5, fontWeight: 600 }}>Prices</span></span>
            <span style={{ fontSize: 4.5, color: '#5a3d40' }}><b>4.9 ★</b> from 380 reviews</span>
          </div>
          <div style={{ position: 'relative', height: '100%', minHeight: 0 }}>
            <div style={{ position: 'absolute', width: '70%', aspectRatio: '1', borderRadius: '50%', background: '#fff8f0', left: '-10%', bottom: '-6%' }} />
            <div style={{ position: 'absolute', width: '30%', aspectRatio: '1', borderRadius: 5, background: '#ffd36e', right: '-6%', top: '10%', transform: 'rotate(14deg)' }} />
            <img src={img} alt="" style={{ position: 'absolute', inset: '0 4% 0 4%', width: '92%', height: '100%', objectFit: 'cover', borderRadius: '999px 999px 6px 6px' }} />
            <span style={{ position: 'absolute', top: '6%', left: '-4%', width: 26, height: 26, borderRadius: '50%', background: '#8c1c31', color: '#fff8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', transform: 'rotate(-12deg)', font: "400 4.5px/1.1 'Gloock', serif" }}>Open late Thu</span>
          </div>
        </div>
      </div>
    );
  }
  if (home && id === 'consultant') {
    return (
      <div style={{ ...root, display: 'flex', flexDirection: 'column', background: '#dce5e1', color: '#1b2423', fontFamily: "'Manrope', sans-serif" }} aria-hidden="true">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 10px' }}>
          <span style={{ font: "400 9px/1 'DM Serif Display', serif", color: '#133b3a' }}>{site}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 5, fontWeight: 600 }}>{navLinks({})}<span style={{ padding: '3px 6px', borderRadius: 999, background: '#133b3a', color: '#efe8dc' }}>Book a call</span></span>
        </div>
        <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 10, padding: '2px 10px 10px', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 4.5, fontWeight: 700, color: '#133b3a' }}><i style={{ width: 8, height: 0.75, background: '#c4673f' }} />Strategy for founders</span>
            <span style={{ font: "400 14px/0.96 'DM Serif Display', serif", color: '#133b3a', letterSpacing: '-0.01em' }}>{line}</span>
            <span style={{ alignSelf: 'flex-start', padding: '3px 7px', borderRadius: 999, background: '#c4673f', color: '#fff', fontSize: 4.5, fontWeight: 700 }}>Book a 20-minute intro</span>
          </div>
          <div style={{ position: 'relative', height: '100%', minHeight: 0 }}>
            <div style={{ position: 'absolute', inset: '6% -4% -4% 8%', borderRadius: '999px 999px 6px 6px', background: '#133b3a' }} />
            <div style={{ position: 'absolute', width: '32%', aspectRatio: '1', borderRadius: '0 0 0 100%', background: '#c4673f', right: '-8%', top: '-4%' }} />
            <img src={img} alt="" style={{ position: 'absolute', inset: '0 6% 4% 0', width: '94%', height: '96%', objectFit: 'cover', borderRadius: '999px 999px 6px 6px' }} />
          </div>
        </div>
      </div>
    );
  }
  if (home && id === 'restaurant') {
    return (
      <div style={{ ...root, background: '#0e0c0a', color: '#ede5d8', fontFamily: "'Jost', sans-serif" }} aria-hidden="true">
        <img src={img} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(14,12,10,0.4) 0%, rgba(14,12,10,0.5) 50%, #0e0c0a 100%)' }} />
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', padding: '7px 10px', fontSize: 4, letterSpacing: '0.2em', textTransform: 'uppercase', borderBottom: '0.5px solid rgba(201,164,92,0.4)' }}>
          <span style={{ display: 'flex', gap: 6 }}>{navLinks({})}</span>
          <span style={{ font: "500 8px 'Bodoni Moda', serif", letterSpacing: '0.3em' }}>{site.toUpperCase()}</span>
          <span onClick={pick(last)} style={{ justifySelf: 'end', padding: '2px 5px', border: '0.5px solid #c9a45c', color: '#c9a45c', cursor: 'pointer' }}>Reserve</span>
        </div>
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 5, padding: '0 12px' }}>
          <span style={{ fontSize: 4, letterSpacing: '0.28em', textTransform: 'uppercase', color: '#c9a45c' }}>Seafood &amp; wine</span>
          <span style={{ font: "italic 400 20px/0.9 'Bodoni Moda', serif" }}>{line}</span>
          <span style={{ width: 0.5, height: 10, background: '#c9a45c' }} />
          <span onClick={pick(last)} style={{ padding: '3px 8px', background: '#c9a45c', color: '#0e0c0a', fontSize: 4, letterSpacing: '0.2em', textTransform: 'uppercase', cursor: 'pointer' }}>Reserve a table</span>
        </div>
      </div>
    );
  }
  if (home && id === 'product') {
    return (
      <div style={{ ...root, display: 'flex', flexDirection: 'column', background: '#fff', color: '#0b0b14', fontFamily: "'Bricolage Grotesque', sans-serif" }} aria-hidden="true">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontWeight: 800, fontSize: 9, letterSpacing: '-0.025em' }}><i style={{ width: 6, height: 6, borderRadius: '50%', background: '#2b35f5', boxShadow: '3px 0 0 #d6ff3d' }} /><span style={{ marginLeft: 3, whiteSpace: 'nowrap' }}>{site.toLowerCase()}</span></span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 5, fontWeight: 500 }}>{navLinks({})}<span style={{ padding: '3px 6px', borderRadius: 999, background: '#0b0b14', color: '#f4f1ea', fontWeight: 600, whiteSpace: 'nowrap' }}>Start free</span></span>
        </div>
        <div style={{ flex: 1, minHeight: 0, margin: '0 6px 6px', borderRadius: 12, background: '#2b35f5', color: '#fff', position: 'relative', overflow: 'hidden', padding: 10 }}>
          <div style={{ position: 'absolute', width: 90, height: 90, borderRadius: '50%', background: '#d6ff3d', right: -30, bottom: -46 }} />
          <div style={{ position: 'absolute', width: 26, height: 26, borderRadius: '50%', background: '#ff6b2c', left: '46%', top: -12 }} />
          <div style={{ position: 'relative', width: '50%', display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ alignSelf: 'flex-start', padding: '2px 5px', borderRadius: 999, background: '#d6ff3d', color: '#0b0b14', fontSize: 4, fontWeight: 600, whiteSpace: 'nowrap' }}>New: auto-reminders</span>
            <span style={{ fontWeight: 800, fontSize: 12, lineHeight: 0.95, letterSpacing: '-0.025em' }}>{line}</span>
            <span style={{ alignSelf: 'flex-start', padding: '3px 7px', borderRadius: 999, background: '#d6ff3d', color: '#0b0b14', fontSize: 4.5, fontWeight: 700 }}>Start free</span>
          </div>
          <div style={{ position: 'absolute', right: 8, top: '28%', width: '36%', padding: 6, borderRadius: 6, background: '#ff9bdb', color: '#0b0b14', transform: 'rotate(7deg)', fontSize: 4, lineHeight: 1.3 }}>Reminder sent · 2 days late</div>
          <div style={{ position: 'absolute', right: 12, top: '42%', width: '36%', padding: 6, borderRadius: 7, background: '#fff', color: '#0b0b14', transform: 'rotate(-3deg)', display: 'flex', flexDirection: 'column', gap: 4, boxShadow: '0 8px 16px -8px rgba(0,0,0,0.5)' }}>
            <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 4.5, fontWeight: 700 }}>Invoice #0142<span style={{ padding: '1px 3px', borderRadius: 999, background: '#d6ff3d', fontSize: 3.5 }}>Paid ✓</span></span>
            <span style={{ height: 1, background: '#ece9e0' }} /><span style={{ height: 1, background: '#ece9e0' }} />
            <span style={{ fontWeight: 800, fontSize: 9, letterSpacing: '-0.03em' }}>£2,400.00</span>
          </div>
        </div>
      </div>
    );
  }
  if (home && id === 'journal') {
    return (
      <div style={{ ...root, display: 'flex', flexDirection: 'column', background: '#f2ebdd', color: '#1e1a15', fontFamily: "'Newsreader', serif", padding: '0 10px', boxSizing: 'border-box' }} aria-hidden="true">
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '0.5px solid #1e1a15', font: `400 4px ${MONO}`, textTransform: 'uppercase', letterSpacing: '0.04em' }}><span>No. 47 · Autumn</span><span style={{ display: 'flex', gap: 6 }}>{navLinks({})}</span><span style={{ color: '#d2462a' }}>Subscribe</span></div>
        <div style={{ textAlign: 'center', font: `italic 400 ${fit(site.length, 0.44, 58, 20)}/0.82 'Instrument Serif', serif`, letterSpacing: '-0.04em', color: '#d2462a', whiteSpace: 'nowrap', overflow: 'hidden', paddingTop: 3 }}>{site}</div>
        <div style={{ textAlign: 'center', padding: '3px 0', marginTop: 3, borderTop: '1.5px double #1e1a15', borderBottom: '0.5px solid #1e1a15', font: `400 4px ${MONO}`, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{line}</div>
        <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '1fr 1.15fr', gap: 8, padding: '7px 0 8px', alignItems: 'center' }}>
          <div style={{ background: '#d2462a', height: '100%', minHeight: 0, position: 'relative', overflow: 'hidden' }}><img src={img} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'grayscale(1) contrast(1.25)', mixBlendMode: 'multiply' }} /></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ font: `400 4px ${MONO}`, textTransform: 'uppercase', color: '#7e7466' }}>Essay · 8 min</span>
            <span style={{ font: "400 12px/0.98 'Instrument Serif', serif" }}>{titles[0] ? titles[0] : <>What the hills taught me about <i style={{ color: '#d2462a' }}>waiting</i></>}</span>
            <span style={{ font: `500 4px ${MONO}`, textTransform: 'uppercase', color: '#d2462a' }}>Read the essay →</span>
          </div>
        </div>
      </div>
    );
  }
  if (home && id === 'magazine') {
    const side = [['Food', h(1, 'The café that only opens when it rains')], ['City', h(2, 'Night buses return in November')], ['Culture', 'A tiny cinema in a laundry']];
    return (
      <div style={{ ...root, display: 'flex', flexDirection: 'column', background: '#fff', color: '#111', fontFamily: "'Source Serif 4', serif", padding: '0 10px', boxSizing: 'border-box' }} aria-hidden="true">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', font: "600 4px 'Archivo Narrow', sans-serif", textTransform: 'uppercase', letterSpacing: '0.06em' }}><span>Thursday 15 October · Issue 212</span><span style={{ padding: '1.5px 4px', background: '#e3241b', color: '#fff' }}>Subscribe</span></div>
        <div style={{ borderTop: '2px solid #111', textAlign: 'center', font: `400 ${fit(site.length, 0.48, 48, 18)}/0.86 'Anton', sans-serif`, textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', paddingTop: 2 }}>{site}</div>
        <div style={{ display: 'flex', gap: 7, padding: '3px 0', marginTop: 2, borderTop: '0.5px solid #111', borderBottom: '2px solid #111', font: "600 4px 'Archivo Narrow', sans-serif", textTransform: 'uppercase', letterSpacing: '0.08em' }}><span>Front page</span>{navLinks({})}</div>
        <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '1.7fr 1fr', gap: 6, padding: '6px 0 8px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minHeight: 0 }}>
            <div style={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden' }}><img src={img} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} /></div>
            <span style={{ font: "600 3.5px 'Archivo Narrow', sans-serif", textTransform: 'uppercase', letterSpacing: '0.1em', color: '#e3241b' }}>City · Cover story</span>
            <span style={{ font: "400 10px/0.9 'Anton', sans-serif", textTransform: 'uppercase' }}>{h(0, 'The street that saved its bakery')}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, borderLeft: '0.5px solid #111', paddingLeft: 6 }}>
            {side.map(([k, t]) => (
              <div key={t} style={{ display: 'flex', flexDirection: 'column', gap: 1, paddingBottom: 4, borderBottom: '0.5px solid #e3e3e3' }}><span style={{ font: "600 3.5px 'Archivo Narrow', sans-serif", textTransform: 'uppercase', letterSpacing: '0.1em', color: '#e3241b' }}>{k}</span><span style={{ font: "600 6px/1.15 'Source Serif 4', serif" }}>{t}</span></div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  if (home && id === 'publisher') {
    const left = [['Politics', h(1, 'Mayor’s budget cuts library hours')], ['Housing', h(2, 'Rents fall for the first time since 2019')], ['Food', 'Ten openings to try this month']];
    const right = [['12 min ago', 'Bridge closes for repairs Saturday'], ['38 min ago', 'Schools name new superintendent'], ['1 hr ago', 'Ferry fares to rise in January'], ['2 hr ago', 'Night market returns to the pier']];
    return (
      <div style={{ ...root, display: 'flex', flexDirection: 'column', background: '#fff', color: '#0b1f3a', fontFamily: "'Literata', serif" }} aria-hidden="true">
        <div style={{ background: '#0b1f3a', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 10px', font: "500 4px 'Schibsted Grotesk', sans-serif" }}><span>Thursday, Oct 15 · 18°</span><span style={{ padding: '1.5px 4px', background: '#ffc93c', color: '#0b1f3a', fontWeight: 700 }}>Subscribe</span></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px 3px' }}><i style={{ width: 8, height: 8, background: '#ffc93c' }} /><span style={{ font: "800 11px/1 'Schibsted Grotesk', sans-serif", letterSpacing: '-0.04em' }}>{site}</span></div>
        <div style={{ display: 'flex', gap: 6, padding: '3px 10px', borderBottom: '0.5px solid #d5dae1', font: "700 4px 'Schibsted Grotesk', sans-serif", textTransform: 'uppercase', letterSpacing: '0.04em' }}><span style={{ color: '#e2512a' }}>Latest</span>{navLinks({})}</div>
        <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '0.9fr 1.6fr 0.9fr', gap: 6, padding: '6px 10px 8px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {left.map(([k, t]) => (
              <div key={t} style={{ display: 'flex', flexDirection: 'column', gap: 1, paddingBottom: 3, borderBottom: '0.5px solid #e3e7ec' }}><span style={{ font: "700 3.5px 'Schibsted Grotesk', sans-serif", textTransform: 'uppercase', color: '#e2512a' }}>{k}</span><span style={{ font: "700 5.5px/1.15 'Schibsted Grotesk', sans-serif" }}>{t}</span></div>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minHeight: 0 }}>
            <div style={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden' }}><img src={img} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} /></div>
            <span style={{ font: "800 9px/1 'Schibsted Grotesk', sans-serif", letterSpacing: '-0.03em' }}>{h(0, line)}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, borderLeft: '0.5px solid #d5dae1', paddingLeft: 5 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 2, font: "800 4.5px 'Schibsted Grotesk', sans-serif", textTransform: 'uppercase' }}><i style={{ width: 3, height: 3, borderRadius: '50%', background: '#e2512a' }} />Latest</span>
            {right.map(([k, t]) => (
              <div key={t} style={{ display: 'flex', flexDirection: 'column', gap: 1 }}><span style={{ font: "500 3.5px 'Schibsted Grotesk', sans-serif", color: '#6a7686' }}>{k}</span><span style={{ font: "700 4.8px/1.15 'Schibsted Grotesk', sans-serif" }}>{t}</span></div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  if (home && id === 'newsroom') {
    const card: CSSProperties = { border: '0.5px solid #e2dfd7', borderRadius: 4, background: '#fff', overflow: 'hidden', minHeight: 0 };
    const kicker = (t: string, color = '#1f5eff') => <span style={{ font: "700 3.5px 'Bricolage Grotesque', sans-serif", textTransform: 'uppercase', letterSpacing: '0.08em', color }}>{t}</span>;
    const briefs = [['News', h(2, 'Bridge reopens two weeks early')], ['Features', 'Inside the last video shop in town'], ['News', 'Storm warning lifted overnight']];
    const ticker = ['Markets close higher', 'Final score 3–1', 'Ferry fares frozen', 'Library hours restored'];
    return (
      <div style={{ ...root, display: 'flex', flexDirection: 'column', background: '#fbfaf7', color: '#14161a', fontFamily: "'Newsreader', serif" }} aria-hidden="true">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '6px 10px', borderBottom: '0.5px solid #e2dfd7' }}>
          <span style={{ display: 'flex', alignItems: 'baseline', gap: 4, minWidth: 0 }}>
            <span style={{ font: "800 9px/1 'Bricolage Grotesque', sans-serif", letterSpacing: '-0.03em', whiteSpace: 'nowrap' }}>{site}</span>
            <span style={{ font: "400 4px 'Newsreader', serif", color: '#5f6670', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{line}</span>
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 'none', font: "500 4.5px 'Bricolage Grotesque', sans-serif" }}>
            {navLinks({})}
            <span style={{ padding: '2.5px 5px', borderRadius: 4, background: '#14161a', color: '#fbfaf7', fontWeight: 700, whiteSpace: 'nowrap' }}>Get the briefing</span>
          </span>
        </div>
        <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '5fr 3fr', gridTemplateRows: '1fr auto', gap: 5, padding: '6px 10px 5px' }}>
          <div style={{ ...card, display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, minHeight: 0, position: 'relative' }}><img src={img} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} /></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1.5, padding: '4px 5px 5px' }}>
              {kicker('News')}
              <span style={{ font: "800 10px/0.98 'Bricolage Grotesque', sans-serif", letterSpacing: '-0.03em' }}>{h(0, 'The night buses are staying, and running later')}</span>
              <span style={{ fontSize: 3.5, color: '#5f6670' }}>2 hr ago · 6 facts</span>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minHeight: 0 }}>
            <div style={{ ...card, flex: 1.3, background: '#f0eee8', border: 'none', display: 'flex', flexDirection: 'column', gap: 2, padding: 5 }}>
              {kicker('Analysis')}
              <span style={{ font: "700 6.5px/1.05 'Bricolage Grotesque', sans-serif", letterSpacing: '-0.02em' }}>{h(1, 'What the rate cut means for your rent')}</span>
              <span style={{ fontSize: 4.5, lineHeight: 1.3, color: '#5f6670' }}>Landlords have a year of cheaper loans ahead. Here is who passes it on.</span>
            </div>
            <div style={{ ...card, flex: 1, borderLeft: '2px solid #ff5a36', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2, padding: '4px 5px' }}>
              <span style={{ font: "italic 400 6px/1.15 'Newsreader', serif" }}>“We kept the lights on for forty years.”</span>
              {kicker('Opinion', '#ff5a36')}
            </div>
          </div>
          <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
            {briefs.map(([k, t]) => (
              <div key={t} style={{ display: 'flex', flexDirection: 'column', gap: 1, paddingTop: 3, borderTop: '1px solid #14161a' }}>{kicker(k)}<span style={{ font: "700 5px/1.15 'Bricolage Grotesque', sans-serif" }}>{t}</span></div>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 10px', background: '#14161a', color: '#fbfaf7', font: "700 3.8px 'Bricolage Grotesque', sans-serif", textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap', overflow: 'hidden' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 2, color: '#ff5a36' }}><i style={{ width: 3, height: 3, borderRadius: '50%', background: '#ff5a36' }} />Live</span>
          {ticker.map((t) => <span key={t}>{t}</span>)}
        </div>
      </div>
    );
  }
  if (home && id === 'nonprofit') {
    return (
      <div style={{ ...root, background: '#12301f', color: '#f4f1ea', fontFamily: "'Barlow', sans-serif" }} aria-hidden="true">
        <img src={img} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(14,26,18,0.8) 0%, rgba(14,26,18,0.35) 60%, rgba(14,26,18,0.1) 100%)' }} />
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 10px', font: "700 4.5px 'Barlow Condensed', sans-serif", textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><i style={{ width: 7, height: 7, borderRadius: '50%', background: '#f2b33d', boxShadow: 'inset 0 -2.5px 0 #3f7d3a' }} /><span style={{ font: "800 8px 'Barlow Condensed', sans-serif" }}>{site.toUpperCase()}</span></span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{navLinks({})}<span style={{ padding: '2px 5px', background: '#f2b33d', color: '#0e1a12' }}>Donate</span></span>
        </div>
        <div style={{ position: 'absolute', left: 10, right: '30%', bottom: 12, display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={{ font: "700 4px 'Barlow Condensed', sans-serif", textTransform: 'uppercase', letterSpacing: '0.14em', color: '#f2b33d' }}>Since 2011</span>
          <span style={{ font: "800 21px/0.82 'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>{line}</span>
          <span style={{ display: 'flex', gap: 3 }}><span style={{ padding: '3px 7px', background: '#f2b33d', color: '#0e1a12', font: "800 4.5px 'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>Donate</span><span style={{ padding: '2.5px 7px', border: '0.5px solid #f4f1ea', font: "800 4.5px 'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>Volunteer</span></span>
        </div>
      </div>
    );
  }

  // ── Any other page: a generic drawing of what the page does ───────────
  const services = SERVICES[id] || SERVICES_DEFAULT;
  const logoFont = LOGO_FONT[id] || `600 9px ${head}`;
  let content: React.ReactNode;
  switch (kind) {
    case 'work':
      content = (
        <>
          <div style={{ font: `800 18px/0.85 ${head}`, letterSpacing: '-0.05em' }}>{hero}</div>
          <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {['1600585154340-be6161a56a0c', '1557672172-298e090bd0f1', '1486406146926-c627a92ad1ab', '1586023492125-27b2c045efd7'].map((p) => (
              <div key={p} style={{ position: 'relative', overflow: 'hidden', background: c.line }}><img src={u(p, 400)} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} /></div>
            ))}
          </div>
        </>
      );
      break;
    case 'menu':
      content = (
        <>
          <div style={{ textAlign: 'center', font: `italic 400 16px/1 ${head}` }}>{hero}</div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 10, fontSize: 4.5, letterSpacing: '0.2em', textTransform: 'uppercase', borderBottom: `0.5px solid ${c.line}`, paddingBottom: 4 }}><span style={{ color: c.accent }}>Dinner</span><span>Wine</span><span>Bar</span></div>
          <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[
              { label: 'From the sea', items: [['Turbot for two', '€68'], ['Red mullet', '€29'], ['Octopus', '€26']] },
              { label: 'To finish', items: [['Pastel de nata', '€5'], ['Bitter chocolate', '€11'], ['Cheese', '€14']] },
            ].map((col) => (
              <div key={col.label} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ font: `italic 400 7px ${head}`, color: c.accent }}>{col.label}</span>
                {col.items.map(([n, p]) => (
                  <span key={n} style={{ display: 'flex', alignItems: 'baseline', gap: 4, fontSize: 6.5 }}><span style={{ fontFamily: head }}>{n}</span><span style={{ flex: 1, borderBottom: `0.5px dotted ${c.line}` }} /><span style={{ color: c.accent }}>{p}</span></span>
                ))}
              </div>
            ))}
          </div>
        </>
      );
      break;
    case 'pricing':
      content = (
        <>
          <div style={{ font: `800 13px/0.9 ${head}`, textAlign: 'center', padding: '0 10%', letterSpacing: '-0.04em' }}>Simple pricing. No per-invoice fees.</div>
          <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 5 }}>
            {[
              { n: 'Starter', p: '£0', bg: c.line, fg: c.text, border: c.text, btn: c.text, btnFg: c.bg },
              { n: 'Pro', p: '£10', bg: c.accent, fg: c.on, border: c.accent, btn: c.accent2, btnFg: c.text },
              { n: 'Studio', p: '£24', bg: c.accent2, fg: c.text, border: c.accent2, btn: c.text, btnFg: c.bg },
            ].map((t) => (
              <div key={t.n} style={{ border: `1px solid ${t.border}`, background: t.bg, color: t.fg, borderRadius: 8, padding: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: 5.5, fontWeight: 700 }}>{t.n}</span>
                <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: '-0.04em' }}>{t.p}</span>
                <span style={{ marginTop: 'auto', padding: 2, borderRadius: 999, textAlign: 'center', fontSize: 4, fontWeight: 700, background: t.btn, color: t.btnFg }}>Start</span>
              </div>
            ))}
          </div>
        </>
      );
      break;
    case 'list': {
      const posts =
        id === 'publisher'
          ? [['NEWS', 'Council votes to keep the night buses'], ['FOOD', 'Ten new openings this month'], ['HOUSING', 'Rents fall for the first time since 2019'], ['ARTS', 'The festival lineup, announced']]
          : [['Oct 12', 'What the hills taught me about waiting'], ['Sep 28', 'A slower kind of Sunday'], ['Sep 14', 'Tomatoes, the last good week'], ['Aug 31', 'Notes from the garden']];
      content = (
        <>
          <div style={{ font: `italic 400 18px/1 ${head}`, color: c.accent }}>{hero}</div>
          <div style={{ display: 'flex', flexDirection: 'column', borderTop: `1.5px double ${c.text}` }}>
            {posts.map(([d, t]) => (
              <div key={t} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '4px 0', borderBottom: `0.5px solid ${c.line}` }}>
                <span style={{ font: `400 4.5px ${MONO}`, color: c.accent, width: 22, flex: 'none' }}>{d}</span>
                <span style={{ font: `400 8px/1.15 ${head}` }}>{t}</span>
              </div>
            ))}
          </div>
        </>
      );
      break;
    }
    case 'cause':
      content = (
        <>
          <div style={{ font: `800 18px/0.85 ${head}`, textTransform: 'uppercase' }}>{hero}</div>
          <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            <div style={{ background: '#12301f', color: '#f4f1ea', padding: 7, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ font: `800 9px ${head}`, textTransform: 'uppercase' }}>Give</span>
              <span style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
                {['£10', '£25', '£50', '£100'].map((a, i) => (
                  <span key={a} style={{ padding: '3px 0', textAlign: 'center', border: '0.5px solid #f2b33d', fontSize: 5, background: i === 1 ? '#f2b33d' : 'transparent', color: i === 1 ? '#0e1a12' : '#f2b33d' }}>{a}</span>
                ))}
              </span>
              <span style={{ marginTop: 'auto', padding: 3, textAlign: 'center', background: '#f2b33d', color: '#0e1a12', font: `800 5px ${head}`, textTransform: 'uppercase' }}>Give £25 monthly</span>
            </div>
            <div style={{ background: '#dce6d3', color: '#12301f', padding: 7, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span style={{ font: `800 9px ${head}`, textTransform: 'uppercase' }}>Volunteer</span>
              <span style={{ height: 7, border: '0.5px solid #12301f', background: '#f4f1ea' }} /><span style={{ height: 7, border: '0.5px solid #12301f', background: '#f4f1ea' }} />
              <span style={{ marginTop: 'auto', padding: 3, textAlign: 'center', background: '#12301f', color: '#f2b33d', font: `800 5px ${head}`, textTransform: 'uppercase' }}>Count me in</span>
            </div>
          </div>
        </>
      );
      break;
    case 'services':
      content = (
        <>
          <div style={{ font: `400 15px/1 ${head}`, color: c.accent }}>{hero}</div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {services.map((sv) => (
              <div key={sv.n} style={{ display: 'flex', alignItems: 'baseline', gap: 6, padding: '5px 0', borderTop: `0.5px solid ${c.line}` }}>
                <span style={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}><span style={{ font: `400 8px/1.2 ${head}` }}>{sv.n}</span><span style={{ fontSize: 5, color: c.sub }}>{sv.d}</span></span>
                <span style={{ fontSize: 6.5, fontWeight: 600, color: c.accent }}>{sv.p}</span>
              </div>
            ))}
          </div>
        </>
      );
      break;
    case 'contact':
      content = (
        <>
          <div style={{ font: `400 15px/1 ${head}`, color: c.accent }}>{hero}</div>
          <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 10 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 5, color: c.sub }}>Name</span><span style={{ height: 11, border: `1px solid ${c.line}`, borderRadius: cardR }} />
              <span style={{ fontSize: 5, color: c.sub }}>Email</span><span style={{ height: 11, border: `1px solid ${c.line}`, borderRadius: cardR }} />
              <span style={{ alignSelf: 'flex-start', marginTop: 3, fontSize: 5.5, fontWeight: 600, padding: '3px 9px', background: c.accent, color: c.on, borderRadius: btnR }}>Send</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 6, lineHeight: 1.35 }}>
              <span style={{ fontWeight: 600 }}>{{ local: '14 Chatsworth Road', restaurant: 'Rua Nova do Carvalho 18', consultant: 'London and remote' }[id] || '12 Harbour Street'}</span>
              <span style={{ color: c.sub }}>hello@{siteAddr || 'yourname'}.com</span>
              <span style={{ color: c.sub }}>Open Tue – Sat</span>
            </div>
          </div>
        </>
      );
      break;
    default: // about
      content = (
        <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 10 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <div style={{ font: `400 13px/1 ${head}`, color: c.accent }}>{hero}</div>
            <span style={{ fontSize: 6, lineHeight: 1.45, color: c.sub }}>{ABOUT_LINE[id] || 'A little about who we are.'}</span>
            <span style={{ height: 1.5, width: '90%', background: c.line, marginTop: 3 }} />
            <span style={{ height: 1.5, width: '75%', background: c.line }} />
          </div>
          <div style={{ position: 'relative', overflow: 'hidden', background: c.line, borderRadius: cardR }}><img src={img2} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} /></div>
        </div>
      );
  }

  return (
    <div style={root} aria-hidden="true">
      <div style={{ height: '100%', boxSizing: 'border-box', padding: '8px 12px 12px', display: 'flex', flexDirection: 'column', gap: 9 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <span onClick={pick('Home')} style={{ font: logoFont, whiteSpace: 'nowrap', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis' }}>{site}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 5.5, color: c.sub, whiteSpace: 'nowrap' }}>
            {nav.map((n) => (
              <span key={n.t} onClick={n.go} style={{ cursor: 'pointer', color: n.on ? c.text : c.sub, fontWeight: n.on ? 600 : 400 }}>
                {n.t}
              </span>
            ))}
            <i onClick={pick(last)} style={{ display: 'block', width: 14, height: 6, background: c.accent, borderRadius: btnR, cursor: 'pointer' }} />
          </span>
        </div>
        {content}
      </div>
    </div>
  );
}
